import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { ConnectorTypeEnum } from '@shared/enums/connector-type';
import { EMPTY_MAP_FILTERS, MapFilters } from './filterTypes';
import { FilterSwitch } from './FilterControls';

const ALL_CONNECTOR_TYPES = Object.values(ConnectorTypeEnum);

interface MapFilterDrawerProps {
  open: boolean;
  filters: MapFilters;
  onClose: () => void;
  onChange: (filters: MapFilters) => void;
}

export const MapFilterDrawer = ({
  open,
  filters,
  onClose,
  onChange,
}: MapFilterDrawerProps) => {
  const { t } = useTranslation();

  const toggleConnector = (type: ConnectorTypeEnum) => {
    onChange({
      ...filters,
      connectorTypes: filters.connectorTypes.includes(type)
        ? filters.connectorTypes.filter((c) => c !== type)
        : [...filters.connectorTypes, type],
    });
  };

  const handleReset = () => {
    onChange({ ...EMPTY_MAP_FILTERS, search: filters.search });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '85%',
          maxWidth: 360,
          backgroundColor: 'grey.900',
          backgroundImage: 'none',
        },
      }}
    >
      <Stack sx={{ height: '100%', p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h6" fontWeight={600} color="common.white">
            {t('stationPageWithSearch.filters')}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'grey.400' }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack sx={{ flex: 1, gap: 3, overflowY: 'auto' }}>
          <Box>
            <Typography
              variant="subtitle2"
              color="grey.400"
              sx={{
                mb: 1.5,
                textTransform: 'uppercase',
                fontSize: 11,
                letterSpacing: '0.5px',
              }}
            >
              {t('stationPageWithSearch.quickFilters')}
            </Typography>
            <Stack gap={1}>
              <FilterSwitch
                label={t('stationPageWithSearch.availableNow')}
                checked={filters.availableNow}
                onChange={() =>
                  onChange({ ...filters, availableNow: !filters.availableNow })
                }
              />
              <FilterSwitch
                label={t('stationPageWithSearch.fastCharging')}
                checked={filters.fastCharging}
                onChange={() =>
                  onChange({ ...filters, fastCharging: !filters.fastCharging })
                }
              />
            </Stack>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="grey.400"
              sx={{
                mb: 1.5,
                textTransform: 'uppercase',
                fontSize: 11,
                letterSpacing: '0.5px',
              }}
            >
              {t('stationPageWithSearch.connectorsTypes')}
            </Typography>
            <Stack gap={0.5}>
              {ALL_CONNECTOR_TYPES.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={filters.connectorTypes.includes(type)}
                      onChange={() => toggleConnector(type)}
                      sx={{
                        color: 'grey.600',
                        '&.Mui-checked': { color: 'green.main' },
                      }}
                    />
                  }
                  label={type}
                  sx={{
                    m: 0,
                    py: 0.5,
                    '& .MuiFormControlLabel-label': {
                      color: 'common.white',
                      fontSize: 14,
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" gap={1.5} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleReset}
            sx={{
              borderColor: 'grey.700',
              color: 'grey.300',
              borderRadius: '12px',
              py: 1.5,
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'grey.800',
              },
            }}
          >
            {t('stationPageWithSearch.reset')}
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={onClose}
            sx={{
              backgroundColor: 'green.main',
              color: 'common.white',
              borderRadius: '12px',
              py: 1.5,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'green.dark',
                boxShadow: 'none',
              },
            }}
          >
            {t('stationPageWithSearch.apply')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};
